import { useState, useEffect } from "react";
import { Plus, Search, Shield, Users, MoreVertical, Edit, Trash2, Copy, ChevronRight, ChevronDown, Check, Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { tokenStore } from "@/lib/api";
import { HelpIcon } from "@/components/HelpIcon";

const SYSTEM_ROLES = [
  { id: "super_admin", name: "Super Admin", type: "system", description: "Platform-level administrator with all permissions", members: 3, permissions: 42 },
  { id: "org_owner", name: "Organization Owner", type: "system", description: "Full control within organization", members: 156, permissions: 38 },
  { id: "admin", name: "Administrator", type: "system", description: "User management, API keys, billing, settings", members: 89, permissions: 32 },
  { id: "developer", name: "Developer", type: "system", description: "API access and key management", members: 423, permissions: 18 },
  { id: "analyst", name: "Analyst", type: "system", description: "Read-only access to analytics", members: 67, permissions: 8 },
  { id: "billing", name: "Billing Manager", type: "system", description: "Billing and subscription management", members: 34, permissions: 12 },
  { id: "viewer", name: "Viewer", type: "system", description: "Read-only dashboard access", members: 201, permissions: 5 },
];

const CUSTOM_ROLES = [
  { id: "senior_dev", name: "Senior Developer", type: "custom", description: "Developer with analytics export", members: 45, permissions: 22 },
  { id: "compliance", name: "Compliance Officer", type: "custom", description: "Audit logs and compliance reports", members: 12, permissions: 15 },
  { id: "support", name: "Support Agent", type: "custom", description: "View tenant issues, no modifications", members: 28, permissions: 10 },
];

const PERMISSION_GROUPS = [
  {
    name: "Dashboard",
    permissions: [
      { id: "dashboard:view", name: "View Dashboard", description: "Access dashboard and KPI widgets" },
      { id: "dashboard:customize", name: "Customize Dashboard", description: "Modify dashboard layout and widgets" },
    ]
  },
  {
    name: "API Keys",
    permissions: [
      { id: "api_keys:create", name: "Create API Keys", description: "Generate new API keys" },
      { id: "api_keys:read", name: "View API Keys", description: "View API key list and details" },
      { id: "api_keys:update", name: "Update API Keys", description: "Modify API key settings" },
      { id: "api_keys:delete", name: "Delete/Revoke API Keys", description: "Revoke or delete API keys" },
    ]
  },
  {
    name: "Analytics",
    permissions: [
      { id: "analytics:view", name: "View Analytics", description: "Access usage analytics and charts" },
      { id: "analytics:export", name: "Export Analytics", description: "Export data to CSV/PDF" },
    ]
  },
  {
    name: "Billing",
    permissions: [
      { id: "billing:view", name: "View Billing", description: "View current plan and invoices" },
      { id: "billing:modify", name: "Modify Billing", description: "Change plan, update payment method" },
    ]
  },
  {
    name: "Plan Management",
    permissions: [
      { id: "plans:view", name: "View Plans", description: "View subscription plans and pricing" },
      { id: "plans:create", name: "Create Plans", description: "Create new subscription plans" },
      { id: "plans:update", name: "Update Plans", description: "Modify existing plans and features" },
      { id: "plans:delete", name: "Delete Plans", description: "Remove subscription plans" },
      { id: "vouchers:manage", name: "Manage Vouchers", description: "Create and manage discount vouchers" },
    ]
  },
  {
    name: "Team Management",
    permissions: [
      { id: "team:view", name: "View Team", description: "View team member list" },
      { id: "team:invite", name: "Invite Members", description: "Invite new team members" },
      { id: "team:manage", name: "Manage Members", description: "Modify roles, remove members" },
    ]
  },
  {
    name: "Settings",
    permissions: [
      { id: "settings:view", name: "View Settings", description: "View organization settings" },
      { id: "settings:modify", name: "Modify Settings", description: "Change organization settings" },
    ]
  },
  {
    name: "Email Configuration",
    permissions: [
      { id: "email:view", name: "View Email Config", description: "View SMTP and email template settings" },
      { id: "email:modify", name: "Modify Email Config", description: "Change SMTP settings and templates" },
      { id: "email:test", name: "Send Test Emails", description: "Send test emails to verify configuration" },
    ]
  },
  {
    name: "Audit & Compliance",
    permissions: [
      { id: "audit:view", name: "View Audit Logs", description: "Access security audit logs" },
      { id: "audit:export", name: "Export Audit Logs", description: "Export audit data" },
    ]
  },
  {
    name: "Webhooks",
    permissions: [
      { id: "webhooks:view", name: "View Webhooks", description: "View webhook endpoints" },
      { id: "webhooks:manage", name: "Manage Webhooks", description: "Create/modify webhook endpoints" },
    ]
  },
  {
    name: "Role Administration",
    permissions: [
      { id: "roles:view", name: "View Roles", description: "View role definitions" },
      { id: "roles:manage", name: "Manage Roles", description: "Create and modify custom roles" },
    ]
  },
  {
    name: "Translation Management",
    permissions: [
      { id: "translations:view", name: "View Translations", description: "View translation keys and values" },
      { id: "translations:manage", name: "Manage Translations", description: "Add, edit, and delete translations" },
      { id: "translations:export", name: "Export/Import Translations", description: "Bulk export and import translations" },
    ]
  },
  {
    name: "Data Archival",
    permissions: [
      { id: "archival:view", name: "View Archival", description: "View archival status and jobs" },
      { id: "archival:manage", name: "Manage Archival", description: "Configure retention policies and run jobs" },
      { id: "archival:restore", name: "Restore Data", description: "Request and restore archived data" },
    ]
  },
];

export default function RoleManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(PERMISSION_GROUPS.map(g => g.name));
  const [baseRole, setBaseRole] = useState("none");
  const [activeTab, setActiveTab] = useState("system");
  const [systemRoles, setSystemRoles] = useState(SYSTEM_ROLES);
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/roles/`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.system_roles?.length > 0) setSystemRoles(data.system_roles);
        }
      } catch { /* keep static data */ }
      finally { setRolesLoading(false); }
    })();
  }, []);

  const allRoles = [...systemRoles, ...CUSTOM_ROLES];

  const filteredSystemRoles = systemRoles.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredCustomRoles = CUSTOM_ROLES.filter(r => 
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => 
      prev.includes(groupName) 
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const togglePermission = (permId) => {
    setSelectedPermissions(prev =>
      prev.includes(permId)
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  const selectAllInGroup = (group) => {
    const groupPermIds = group.permissions.map(p => p.id);
    const allSelected = groupPermIds.every(id => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(p => !groupPermIds.includes(p)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...groupPermIds])]);
    }
  };

  const handleCreateRole = () => {
    setRoleName("");
    setRoleDescription("");
    setSelectedPermissions([]);
    setBaseRole("none");
    setSelectedRole(null);
    setCreateDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setRoleName(role.name);
    setRoleDescription(role.description);
    setSelectedRole(role);
    // Simulate loading permissions for this role
    setSelectedPermissions(["dashboard:view", "api_keys:read", "analytics:view"]);
    setCreateDialogOpen(true);
  };

  const handleBaseRoleChange = (value) => {
    setBaseRole(value);
    if (value !== "none") {
      // Simulate inheriting permissions from base role
      const basePermissions = {
        "developer": ["dashboard:view", "api_keys:create", "api_keys:read", "api_keys:update", "analytics:view"],
        "analyst": ["dashboard:view", "analytics:view", "analytics:export"],
        "viewer": ["dashboard:view"],
      };
      setSelectedPermissions(basePermissions[value] || []);
    }
  };

  const RoleCard = ({ role, isCustom }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`role-card-${role.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isCustom ? 'bg-purple-100' : 'bg-blue-100'}`}>
            <Shield className={`w-5 h-5 ${isCustom ? 'text-purple-600' : 'text-blue-600'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-slate-900 text-sm">{role.name}</h3>
              <Badge variant={isCustom ? "secondary" : "outline"} className="text-[10px]">
                {isCustom ? "Custom" : "System"}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEditRole(role)}>
              <Edit className="w-4 h-4 mr-2" /> {isCustom ? "Edit" : "View"} Role
            </DropdownMenuItem>
            {isCustom && (
              <>
                <DropdownMenuItem><Copy className="w-4 h-4 mr-2" /> Duplicate</DropdownMenuItem>
                <DropdownMenuItem className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1 text-slate-500">
            <Users className="w-3.5 h-3.5" /> {role.members} members
          </span>
          <span className="flex items-center gap-1 text-slate-500">
            <Shield className="w-3.5 h-3.5" /> {role.permissions} permissions
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="role-management-page">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="role-management-title">Role Management</h1>
          <HelpIcon featureTag="rbac" portalType="admin" />
        </div>
        <p className="text-sm text-slate-500 mt-1">Define roles and manage permissions for platform access control</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6 gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="role-search"
          />
        </div>
        <Button onClick={handleCreateRole} data-testid="create-role-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Custom Role
        </Button>
      </div>

      {/* Role Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="system">System Roles ({systemRoles.length})</TabsTrigger>
          <TabsTrigger value="custom">Custom Roles ({CUSTOM_ROLES.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="system" className="m-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="system-roles-grid">
            {filteredSystemRoles.map(role => (
              <RoleCard key={role.id} role={role} isCustom={false} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="custom" className="m-0">
          {filteredCustomRoles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="custom-roles-grid">
              {filteredCustomRoles.map(role => (
                <RoleCard key={role.id} role={role} isCustom={true} />
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-medium text-slate-900 mb-1">No custom roles yet</h3>
              <p className="text-sm text-slate-500 mb-4">Create custom roles to define specific permission sets for your team</p>
              <Button onClick={handleCreateRole}>
                <Plus className="w-4 h-4 mr-2" /> Create Custom Role
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle>{selectedRole ? (selectedRole.type === "system" ? "View Role" : "Edit Role") : "Create Custom Role"}</DialogTitle>
            <DialogDescription>
              {selectedRole?.type === "system" 
                ? "System roles cannot be modified. You can create a custom role based on this one."
                : "Define the name, description, and permissions for this role."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Role Details */}
            <div className="w-1/3 border-r p-4 space-y-4">
              <div>
                <Label className="text-xs">Role Name</Label>
                <Input
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g., Senior Developer"
                  className="mt-1"
                  disabled={selectedRole?.type === "system"}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  placeholder="Describe what this role is for..."
                  className="mt-1 resize-none"
                  rows={3}
                  disabled={selectedRole?.type === "system"}
                />
              </div>
              {!selectedRole && (
                <div>
                  <Label className="text-xs">Copy Permissions From</Label>
                  <Select value={baseRole} onValueChange={handleBaseRoleChange}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Start from scratch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Start from scratch</SelectItem>
                      <SelectItem value="developer">Developer</SelectItem>
                      <SelectItem value="analyst">Analyst</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Selected Permissions</span>
                  <Badge variant="secondary">{selectedPermissions.length}</Badge>
                </div>
              </div>
            </div>
            
            {/* Right Panel - Permissions */}
            <div className="flex-1 overflow-auto p-4" data-testid="permissions-panel">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-sm font-medium">Permissions</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSelectedPermissions([])}>
                    Deselect All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedPermissions(PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.id)))}>
                    Select All
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                {PERMISSION_GROUPS.map(group => {
                  const isExpanded = expandedGroups.includes(group.name);
                  const groupPermIds = group.permissions.map(p => p.id);
                  const selectedCount = groupPermIds.filter(id => selectedPermissions.includes(id)).length;
                  const allSelected = selectedCount === groupPermIds.length;
                  
                  return (
                    <div key={group.name} className="border rounded-lg overflow-hidden">
                      <div
                        className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100"
                        onClick={() => toggleGroup(group.name)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                          <span className="font-medium text-sm text-slate-900">{group.name}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {selectedCount}/{groupPermIds.length}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => { e.stopPropagation(); selectAllInGroup(group); }}
                          disabled={selectedRole?.type === "system"}
                        >
                          {allSelected ? "Deselect All" : "Select All"}
                        </Button>
                      </div>
                      {isExpanded && (
                        <div className="divide-y">
                          {group.permissions.map(perm => (
                            <TooltipProvider key={perm.id}>
                              <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50">
                                <div className="flex items-center gap-3">
                                  <Checkbox
                                    checked={selectedPermissions.includes(perm.id)}
                                    onCheckedChange={() => togglePermission(perm.id)}
                                    disabled={selectedRole?.type === "system"}
                                    data-testid={`permission-${perm.id}`}
                                  />
                                  <div>
                                    <span className="text-sm text-slate-700">{perm.name}</span>
                                    <p className="text-xs text-slate-400">{perm.id}</p>
                                  </div>
                                </div>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Info className="w-4 h-4 text-slate-300 hover:text-slate-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs text-xs">{perm.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter className="px-6 py-4 border-t">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            {selectedRole?.type === "system" ? (
              <Button onClick={() => { setSelectedRole(null); setRoleName(selectedRole.name + " (Copy)"); }}>
                Create Copy
              </Button>
            ) : (
              <Button data-testid="save-role-btn">
                <Check className="w-4 h-4 mr-2" /> {selectedRole ? "Save Changes" : "Create Role"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
